import { useEffect, useState, useMemo } from "react";
import { ITEMS } from "game/data/items";
import { listInventoryItems } from "@/services/inventory/list-inventory-items";
import { purchaseItem } from "@/services/shop/purchase-item";
import { useAuth } from "@/providers/auth-provider";
import { useUser } from "@/providers/user-provider";
import { PlayerItem } from "prisma-layer";
import { Alert } from "@/components/alert/alert";
import { ShopBuyConfirmDialog } from "@/components/ui/dialog/shop-buy-confirm-dialog";
import {
  getCurrentMerchant,
  generateMerchantItems,
  formatTimeUntilNextRotation,
  getMerchantTypeDescription,
  type ClientMerchantDefinition,
} from "@/utils/merchant-config";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { Direction } from "types/battle-engine/battle";
import { EnvironmentalEffects } from "@/components/environment/environmental-effects";
import { NightOverlay } from "@/components/environment/lighting-effects";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { Button } from "@/components/ui/buttons/button";
import classNames from "classnames";
import { StandardFrame } from "@/components/ui/frames/standard-frame";

const SuccessOverlay = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm transition-opacity duration-300 ${
        visible ? "opacity-100" : "opacity-0"
      }`}
    >
      <div
        className={`flex flex-col items-center gap-4 transition-all duration-500 ease-out transform ${
          visible
            ? "scale-100 opacity-100 translate-y-0"
            : "scale-50 opacity-0 translate-y-10"
        }`}
      >
        <h2 className="text-4xl font-bold text-white tracking-widest drop-shadow-[0_0_10px_rgba(255,255,255,0.5)] ">
          PURCHASED!
        </h2>
      </div>
    </div>
  );
};

interface MerchantItem {
  item_name: string;
  price: number;
  quantity: number;
}

const MerchantItemCard = ({
  item,
  onBuy,
  isLoading,
}: {
  item: MerchantItem;
  onBuy: (itemName: string) => void;
  isLoading: boolean;
}) => {
  const itemData = ITEMS.find((i) => i.item_name === item.item_name);
  const displayName = item.item_name
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return (
    <div
      className="bg-pk-gray-2/50 backdrop-blur-sm rounded-lg p-1 cursor-pointer group"
      onClick={() => !isLoading && onBuy(item.item_name)}
    >
      <div className="flex flex-col items-center gap-3 mb-2">
        <img
          src={`/assets/items/${item.item_name}.png`}
          alt={displayName}
          className="w-12 h-12 object-contain group-hover:scale-110 transition-transform duration-200"
        />

       <div className="h-6 w-9 rounded-full bg-black/20 scale-y-25 -my-5"></div>


        <div className="flex-1 mt-1">
          <h3 className="text-pk-gray-4  text-[10px] group-hover:text-yellow-300 transition-colors">
            {displayName}
          </h3>
       
        </div>
      </div>
      <div className="flex items-center w-full justify-center">
        <span className="text-pk-garnet-5 font-bold text-lg">
          ${item.price}
        </span>
     
      </div>
    </div>
  );
};

export const ShopView = () => {
  const { getAccessToken } = useAuth();
  const { pokeCoins, setPokeCoins, state: userState } = useUser();

  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [playerItems, setPlayerItems] = useState<
    Pick<PlayerItem, "id" | "item_name" | "amount">[]
  >([]);
  const [merchantItems, setMerchantItems] = useState<MerchantItem[]>([]);
  const isBalanceLoading = userState.status === "loading";
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const [isShopOpen, setIsShopOpen] = useState(false);

  // Get current merchant
  const currentMerchant = useMemo(() => getCurrentMerchant(), []);
  const timeUntilRotation = useMemo(() => formatTimeUntilNextRotation(), []);

  // Fetch player data and generate merchant items on mount
  useEffect(() => {
    fetchPlayerData();
    if (currentMerchant) {
      const items = generateMerchantItems(currentMerchant);
      setMerchantItems(items);
    } else {
      setMerchantItems([]);
    }
  }, [currentMerchant]);

  const fetchPlayerData = async () => {
    const accessToken = getAccessToken();
    if (!accessToken) return;

    try {
      const itemsResponse = await listInventoryItems(accessToken);
      setPlayerItems(itemsResponse.data?.player_items ?? []);
    } catch (error) {
      console.error("Failed to fetch player data:", error);
    }
  };

  const handleBuyClick = (itemName: string) => {
    setSelectedItem(itemName);
    setIsConfirmOpen(true);
  };

  const handleConfirmPurchase = async (amount: number) => {
    if (!selectedItem || isLoading) return;

    const item = ITEMS.find((i) => i.item_name === selectedItem);
    if (!item) return;

    const merchantItem = merchantItems.find((mi) => mi.item_name === selectedItem);
    if (!merchantItem) return;

    const totalPrice = merchantItem.price * amount;
    if (pokeCoins === null || pokeCoins < totalPrice) return;

    const accessToken = getAccessToken();
    if (!accessToken) return;

    setIsLoading(true);
    try {
      const response = await purchaseItem(accessToken, selectedItem, amount);

      if (response.ok && response.data) {
        // Update player items
        setPlayerItems(response.data.player_items);

        // Update poke coins
        setPokeCoins((prev) => prev - totalPrice);

        // Update merchant stock
        setMerchantItems(prev =>
          prev.map(mi =>
            mi.item_name === selectedItem
              ? { ...mi, quantity: mi.quantity - amount }
              : mi
          ).filter(mi => mi.quantity > 0)
        );

        // Reset selection and close dialog
        setIsConfirmOpen(false);
        setSelectedItem(null);

        // Show success animation
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);

        console.log("✅ Purchase successful!");
      } else {
        console.error("❌ Purchase failed:", response.message);
        Alert(response.message || "Purchase failed");
      }
    } catch (error) {
      console.error("❌ Purchase error:", error);
      Alert("An error occurred during purchase");
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectedItemData = () => {
    if (!selectedItem) return null;

    const merchantItem = merchantItems.find(mi => mi.item_name === selectedItem);
    if (!merchantItem) return null;

    const item = ITEMS.find((i) => i.item_name === selectedItem);
    if (!item) return null;

    // Return item with merchant's price
    return {
      ...item,
      shop_price: merchantItem.price,
    };
  };

  return (
    <section
      className="w-full min-h-screen relative z-10 flex flex-col"
  
    >

<EnvironmentalEffects weather="clouds" />
<NightOverlay />

      {showSuccess && <SuccessOverlay />}

      <img src="/assets/backgrounds/merchant.jpg" alt="Shop Background" className="absolute top-0 left-0 w-[480px] h-full object-cover" />

      <ShopBuyConfirmDialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        item={getSelectedItemData()}
        initialAmount={1}
        balance={pokeCoins ?? 0}
        onConfirm={handleConfirmPurchase}
        isLoading={isLoading}
      />

      {/* Poke Coins Display */}
      <div className="absolute top-4 right-4 z-50">
        <div className="bg-pk-gray-2 backdrop-blur-sm rounded-lg p-3 border-4 border-pk-yellow-3">
          <div className="flex items-center gap-3">
            <img
              src="/assets/app-ui/poke-coin.png"
              alt="Coins"
              className="w-8 h-8 object-contain"
            />
            <span className="text-pk-yellow-3 font-bold text-xl">
              {isBalanceLoading ? (
                <span className="inline-flex items-center">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-pk-yellow-3 border-t-transparent" />
                </span>
              ) : (
                pokeCoins
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Merchant Pokemon Display */}
      {currentMerchant ? (
        <div className=" absolute top-2/3 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center">
          {!isShopOpen && <MerchantName merchant={currentMerchant} />}
  
          {/* Merchant Sprite */}
          <PokemonSprite
            pokemon={{ base_pokemon_name: currentMerchant.pokemon_name }}
            style={{ scale: 4 }}
            className="w-48 h-48"
            showShadow={true}
            direction={Direction.SE}
            animation="Idle"
          />

          {isShopOpen ? <MerchantName merchant={currentMerchant} className="transition-all duration-200 translate-y-[100px] z-50 relative" /> : <Button variant="orange" className="translate-y-[30px] z-50 relative" onClick={() => setIsShopOpen(true)}>
            Open Shop
          </Button>}
        </div>
      ) : (
        <div className="absolute top-2/3 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center">
          <StrokedText strokeWidth={12} fontSize={24} className="text-white text-4xl font-bold">
            No Merchant Available
          </StrokedText>
          <StrokedText strokeWidth={9} fontSize={14} className="text-white text-xl font-bold translate-y-[10px]">
            Check back later!
          </StrokedText>
        </div>
      )}

      {/* Items Grid */}
      <div className={classNames("transition-all duration-200 absolute top-24 left-4 right-4 z-50", 
        isShopOpen ? "translate-y-0" : "translate-y-[800px]"
      )}>
        <StandardFrame variant="garnet">
        <div className="">
          
          <button className="absolute z-60 rounded-sm border-2 border-pk-gray-4 bg-pk-red-4 px-2 py-1 text-center flex items-center justify-center -top-2 -right-2 text-white text-lg font-bold" 
          onClick={() => setIsShopOpen(false)}>
            X
          </button>
          
      
          <div className="grid grid-cols-3 gap-3 max-h-[400px] overflow-y-auto">
            {merchantItems.map((item) => (
              <MerchantItemCard
                key={item.item_name}
                item={item}
                onBuy={handleBuyClick}
                isLoading={isLoading}
              />
            ))}
          </div>
          {merchantItems.length === 0 && (
            <div className="text-white/60 text-center py-8">
              No items available right now. Check back later!
            </div>
          )}
        </div>
        </StandardFrame>
      </div>
    </section>
  );
};



const MerchantName = ({ merchant, className }: { merchant: ClientMerchantDefinition, className?: string }) => {
  return <div className={classNames("flex flex-col items-center justify-center gap-0", className)}>
    <StrokedText strokeWidth={12} fontSize={24} className="text-white text-4xl font-bold translate-y-[-75px]">
            {merchant.display_name}
          </StrokedText>
          <StrokedText strokeWidth={9} fontSize={14} className="text-white text-4xl font-bold translate-y-[-75px]">
            {merchant.description}
          </StrokedText>
  </div>
}